/**
 * Image hover effect handler for images with alt-image attribute
 * Swaps to alternate image on hover and back to original on mouse leave
 * Only applies on non-mobile devices
 */
(function() {
  /**
   * Generate srcset for alternate image based on original srcset pattern
   * @param {string} altImageUrl - The alternate image URL
   * @param {string} originalSrcset - The original srcset string
   * @returns {string} Generated srcset for alternate image
   */
  function generateAltSrcset(altImageUrl, originalSrcset) {
    // Extract base URL without width parameter
    const baseUrl = altImageUrl.split('?')[0];
    const params = altImageUrl.split('?')[1] || '';

    // Extract widths from original srcset
    const widthMatches = originalSrcset.match(/width=(\d+)/g);
    if (!widthMatches) return '';

    const widths = widthMatches.map(match => match.replace('width=', ''));

    // Generate new srcset
    return widths.map(width => {
      const url = `${baseUrl}?${params.replace(/width=\d+/, `width=${width}`)}`;
      return `${url} ${width}w`;
    }).join(', ');
  }

  /**
   * Initialize hover effect for images with alt-image attribute
   * @param {Element} container - Optional container to search within (defaults to document)
   */
  function initAltImageHover(container) {
    // Don't apply hover effect on mobile
    const isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (isMobile) return;

    const root = container || document;
    const images = root.querySelectorAll('img[alt-image]');

    images.forEach(function(img) {
      // Skip if already initialized
      if (img.dataset.altImageInitialized) return;

      const altImage = img.getAttribute('alt-image');

      // Skip if no alt-image attribute value
      if (!altImage) return;

      img.dataset.altImageInitialized = 'true';

      // Store original values as data attributes for reliable restoration
      if (!img.dataset.originalSrc) {
        img.dataset.originalSrc = img.src;
      }
      if (!img.dataset.originalSrcset) {
        img.dataset.originalSrcset = img.srcset || '';
      }

      const originalSrc = img.dataset.originalSrc;
      const originalSrcset = img.dataset.originalSrcset;

      // Generate alt srcset from alt-image URL
      const altSrcset = originalSrcset ? generateAltSrcset(altImage, originalSrcset) : '';

      // Remove previous listeners if they exist
      if (img._altImageMouseEnterHandler) {
        img.removeEventListener('mouseenter', img._altImageMouseEnterHandler);
      }
      if (img._altImageMouseLeaveHandler) {
        img.removeEventListener('mouseleave', img._altImageMouseLeaveHandler);
      }

      // Define and store named handler functions
      img._altImageMouseEnterHandler = function() {
        img.src = altImage;
        if (altSrcset) {
          img.srcset = altSrcset;
        }
      };
      img._altImageMouseLeaveHandler = function() {
        img.src = originalSrc;
        if (originalSrcset) {
          img.srcset = originalSrcset;
        }
      };

      img.addEventListener('mouseenter', img._altImageMouseEnterHandler);
      img.addEventListener('mouseleave', img._altImageMouseLeaveHandler);
    });
  }

  // Initialize on DOM ready
  document.addEventListener('DOMContentLoaded', function() {
    initAltImageHover();
  });

  // Also handle dynamically added images with alt-image attribute
  if ('MutationObserver' in window && document.body) {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            // Check if the node itself has alt-image attribute
            if (node.matches && node.matches('img[alt-image]')) {
              initAltImageHover(node.parentElement);
            }
            // Check children for images with alt-image attribute
            const images = node.querySelectorAll && node.querySelectorAll('img[alt-image]');
            if (images && images.length > 0) {
              initAltImageHover(node);
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Expose globally for dynamic content
  window.initAltImageHover = initAltImageHover;
})();
